import React, { useEffect, useState } from "react"
import { ActivityIndicator, ScrollView, StatusBar, Text, TouchableOpacity, View } from "react-native"
import styles from "./styles"
import { Button } from "@Components/CustomButton/Button"
import { BreeSerifText } from "@Components/CustomText/BreeSerifText"
import { OpenSansText } from "@Components/CustomText/OpenSansText"
import { baseColors, baseFontFamily } from "@Constants/baseStyles"
import { Switch } from "react-native-paper"
import { useRecoilValue } from "recoil"
import { userAtom } from "@Recoil/Atom/userAtom"
import { useConsentHooks } from "../../Hooks/useConsentHooks"
import { useNavigation } from "@react-navigation/native"

export const SwitchContent = ({ disabled, color, status, onPress }) =>
    <Switch
        disabled={disabled}
        color={color}
        onValueChange={onPress}
        value={status}
    />

const FirstConsent = ({}) => {
    const navigation = useNavigation()
    const user = useRecoilValue(userAtom)
    const [condition, setCondition] = useState(false)
    const [isUserMajor, setIsUserMajor] = useState(false)
    const [appServices, setAppServices] = useState(false)
    const [statistics, setStatistics] = useState(false)
    const [communication, setCommunication] = useState(false)
    const handleCheckCondition = () => setCondition((prev) => !prev)
    const handleCheckMajor = () => setIsUserMajor((prev) => !prev)
    const canSubmit = () => condition === true && isUserMajor === true
    
    const {
        handleFetchConsent,
        patientConsent,
        fetchingPatientConsent: fetching,

        performSaveConsent,
        saving,
        updateData
    } = useConsentHooks()

    useEffect(() => {
        handleFetchConsent()
    }, [user, updateData])

    useEffect(() => {
        if (patientConsent) {
            setCondition(patientConsent?.condition || null)
            setIsUserMajor(patientConsent?.isUserMajor || null)
            setAppServices(patientConsent?.appServices || null)
            setStatistics(patientConsent?.statistics || null)
            setCommunication(patientConsent?.communication || null)
        }
    }, [patientConsent])

    const payload = {
        cgu: condition,
        major: isUserMajor,
        services: appServices ?? false,
        statistics: statistics ?? false,
        communication: communication ?? false
    }

    useEffect(() => {
        const hasConsent =
            patientConsent?.statistics !== null &&
            patientConsent?.communication !== null &&
            patientConsent?.appServices !== null
        if (hasConsent) {
            navigation.navigate("Main")
        }
    }, [patientConsent])

    return (
        <View style={[styles.container, { paddingTop: StatusBar.currentHeight }]}>
            <ScrollView
                keyboardShouldPersistTaps={"handled"}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: 100 }}
            >
                <View style={styles.content}>
                    {fetching ? (
                        <View style={{ flex: 1, justifyContent: "center", alignContent: "center" }}>
                            <ActivityIndicator color={baseColors.green.primary} size="large" style={{ padding: 20 }} />
                            <OpenSansText
                                defaultFontFamily={baseFontFamily.OpenSansRegular}
                                style={{ textAlign: "center" }}
                                fontSize={16}
                            >
                                Chargement des données en cours...
                            </OpenSansText>
                        </View>
                    ) : (
                        <>
                            <View style={styles.header}>
                                <BreeSerifText style={styles.title}>Recueil de consentement</BreeSerifText>
                                <OpenSansText defaultFontFamily={baseFontFamily.OpenSansRegular} fontSize={14}>
                                    Gérez vos données personnelles.
                                </OpenSansText>
                            </View>

                            <OpenSansText
                                defaultFontFamily={baseFontFamily.OpenSansSemiBold}
                                fontSize={14}
                                style={{ marginBottom: 18 }}
                            >
                                Cochez les cases suivants pour pouvoir utiliser l'application IVEHTE.
                            </OpenSansText>

                            <View onPress={handleCheckCondition} style={styles.firstCheckBoxContent}>
                                <SwitchContent
                                    status={condition}
                                    onPress={handleCheckCondition}
                                    color={baseColors.orange.primary}
                                    disabled={fetching}
                                />

                                <OpenSansText
                                    defaultFontFamily={baseFontFamily.OpenSansSemiBold}
                                    fontSize={14}
                                    style={styles.checkBoxLabel}
                                >
                                    J'accepte les présentes{" "}
                                    <Text
                                        onPress={() => console.log("pressed")}
                                        style={{ textDecorationLine: "underline" }}
                                    >
                                        conditions générales d'utilisation
                                    </Text>{" "}
                                    et créé un compte personnel.
                                </OpenSansText>
                            </View>

                            <View style={styles.secondCheckBoxContent}>
                                <SwitchContent
                                    status={isUserMajor}
                                    onPress={() => setIsUserMajor(!isUserMajor)}
                                    color={baseColors.green.primary}
                                    disabled={fetching}
                                />
                                <OpenSansText
                                    defaultFontFamily={baseFontFamily.OpenSansSemiBold}
                                    fontSize={14}
                                    style={styles.checkBoxLabelGreen}
                                >
                                    Je certifie être majeur, ne bénéficiant pas d'une mesure de tutelle{" "}
                                </OpenSansText>
                            </View>

                            <OpenSansText
                                defaultFontFamily={baseFontFamily.OpenSansSemiBold}
                                fontSize={14}
                                style={{ marginBottom: 18 }}
                            >
                                Les traitements de vos données sont réalisés conformément à la réglementation sur la
                                protection des données.
                            </OpenSansText>

                            <OpenSansText
                                defaultFontFamily={baseFontFamily.OpenSansSemiBold}
                                fontSize={14}
                                style={{ marginBottom: 18 }}
                            >
                                Je consens expressément à ce que IVEHTE mette en oeuvre un traitement de données à
                                caractère personnel me concernant relevant de catégories particulières et notamment des
                                données concernant ma santé et mon régime alimentaire, selon les modalités et sous
                                réserve de mes droits présentés à l'article “Protection des données à caractère
                                personnel” des CGU :
                            </OpenSansText>

                            <View style={styles.thirstCheckBoxContent}>
                                <SwitchContent
                                    status={appServices}
                                    onPress={setAppServices}
                                    color={baseColors.orange.primary}
                                    disabled={fetching}
                                />
                                <OpenSansText
                                    defaultFontFamily={baseFontFamily.OpenSansSemiBold}
                                    fontSize={14}
                                    style={styles.checkBoxLabel}
                                >
                                    Pour la gestion des services fournis par IVEHTE et notamment pour la gestion de
                                    l'accès et du fonctionnement du site IVEHTE et de l'application IVEHTE.
                                </OpenSansText>
                            </View>

                            <View style={styles.thirstCheckBoxContent}>
                                <SwitchContent
                                    status={statistics}
                                    onPress={setStatistics}
                                    color={baseColors.green.primary}
                                    disabled={fetching}
                                />
                                <OpenSansText
                                    defaultFontFamily={baseFontFamily.OpenSansSemiBold}
                                    fontSize={14}
                                    style={styles.checkBoxLabelGreen}
                                >
                                    Pour la réalisation de statistiques concernant l'activité du site IVEHTE et de
                                    l'application IVEHTE ainsi que l'utilisation des services fournis par IVEHTE. A cet
                                    effet, mes données peuvent être anonymisées.
                                </OpenSansText>
                            </View>

                            <View style={styles.thirstCheckBoxContent}>
                                <SwitchContent
                                    status={communication}
                                    onPress={setCommunication}
                                    color={baseColors.orange.primary}
                                    disabled={fetching}
                                />
                                <OpenSansText
                                    defaultFontFamily={baseFontFamily.OpenSansSemiBold}
                                    fontSize={14}
                                    style={styles.checkBoxLabel}
                                >
                                    Pour la communication d'offres commerciales de la part d'IVEHTE ou de ses
                                    partenaires
                                </OpenSansText>
                            </View>
                        </>
                    )}
                </View>
            </ScrollView>

            <Button
                marginTop={5}
                loading={saving}
                onPress={() => performSaveConsent({ payload })}
                text="valider"
                width="60%"
                disabled={saving || fetching || !canSubmit()}
                backgroundColor={baseColors.green.primary}
                labelColor={"white"}
            />
        </View>
    )
}

export default FirstConsent
